import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StarIcon from "@mui/icons-material/Star";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Paper, Typography } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import ManualValidationInput from "../../common/components/ManualValidationInput";
import CrosswordFillCellsBlock from "./CrosswordFillCellsBlock";
import { calculateProgress } from "../../common/utils/PuzzlePlayLogic";
import { SURFACE } from "../../../../dark-theme/palette";

/**
 * Crossword progress accordion first, then admin “fill in the cells” strip when `isAdminRole`.
 * Under Word Clues when `placement="sidebar"`; fallback under grid when clues panel is closed (`underGrid`).
 */
export default function CrosswordPlayProgressAside({
    placement,
    isAdminRole,
    wordInputs,
    localWordInputValues,
    hintRevealCells,
    foundCellKeySet,
    onWordInputChange,
    onWordInputSubmit,
    adminGridClueNumberFontPx,
    adminGridCellFontPx,
    onAdminGridClueNumberFontPxChange,
    onAdminGridCellFontPxChange,
    t,
    manualValidation,
    typedWord,
    onTypedWordChange,
    onTypedWordKeyDown,
    userRole,
    isCrosswordComplete,
    puzzleWordsLength,
    wordsFoundLength,
    settingsLoaded,
    isAuthenticated,
    completionActionsDisabled,
    setFeedbackDialogOpen,
    handleBackClick,
    handleSignUpClick,
}) {
    const wrapSx =
        placement === "sidebar"
            ? {
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  flexShrink: 0,
              }
            : { width: "100%", mt: 1, display: "flex", flexDirection: "column", gap: 2 };

    return (
        <Box sx={wrapSx}>
            <Accordion
                defaultExpanded={false}
                disableGutters
                elevation={0}
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    "&:before": { display: "none" },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                    backgroundColor: "background.paper",
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#3BA666" }} />}
                    sx={{
                        px: 2,
                        minHeight: 48,
                        color: "#3BA666",
                        "& .MuiAccordionSummary-content": {
                            my: 1,
                            alignItems: "center",
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                            width: "100%",
                            color: "#3BA666",
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: "0.5px", color: "inherit" }}>
                            {t("nav.crossword")}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: "inherit",
                            }}
                        >
                            <Box component="span" sx={{ fontWeight: 600, color: "inherit" }}>
                                ({puzzleWordsLength} {t("puzzle.playCross.words")})
                            </Box>
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 2 }}>
                    {Object.keys(wordInputs).length === 0 ? (
                        <Box sx={{ mt: 0, mb: 1, px: 1, textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary">
                                {t("puzzle.playCross.select-cell-hint")}
                            </Typography>
                        </Box>
                    ) : null}

                    {!isCrosswordComplete && (
                        <ManualValidationInput
                            manualValidation={manualValidation}
                            typedWord={typedWord}
                            onTypedWordChange={onTypedWordChange}
                            onTypedWordKeyDown={onTypedWordKeyDown}
                            userRole={userRole}
                        />
                    )}

                    <Box sx={{ mt: 1, width: "100%" }}>
                        <Paper sx={{ p: 2, backgroundColor: SURFACE.sunken, border: `1px solid ${SURFACE.border}` }}>
                            {isCrosswordComplete ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", sm: "row" },
                                            justifyContent: "space-between",
                                            alignItems: { xs: "stretch", sm: "center" },
                                            gap: 2,
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ fontWeight: "bold", flex: 1, wordBreak: "break-word" }}>
                                            {t("puzzle.playCross.all-found")}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                flexShrink: 0,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                disabled={completionActionsDisabled}
                                                onClick={() => setFeedbackDialogOpen(true)}
                                                sx={{
                                                    px: 2,
                                                    py: 0.75,
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    fontSize: "0.875rem",
                                                }}
                                            >
                                                {t("puzzle.playCross.feedback")}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={completionActionsDisabled}
                                                onClick={handleBackClick}
                                                sx={{
                                                    px: 2,
                                                    py: 0.75,
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    fontSize: "0.875rem",
                                                    backgroundColor: "#3BA666",
                                                    "&:hover": {
                                                        backgroundColor: "#2E8B57",
                                                    },
                                                }}
                                            >
                                                {t("puzzle.playCross.complete")}
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Alert
                                        severity="info"
                                        variant="outlined"
                                        icon={<InfoOutlinedIcon fontSize="small" />}
                                        sx={{ py: 0.75, fontSize: "0.8125rem", alignItems: "center" }}
                                    >
                                        {t("puzzle.playCross.browser-refresh-hint")}
                                    </Alert>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {t("puzzle.playCross.find-all-words")}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={puzzleWordsLength ? calculateProgress(wordsFoundLength, puzzleWordsLength) : 0}
                                            sx={{ height: 4, borderRadius: 2, flex: 1 }}
                                        />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ minWidth: "fit-content", fontWeight: 500 }}
                                        >
                                            {wordsFoundLength} / {puzzleWordsLength}
                                        </Typography>
                                    </Box>
                                    {settingsLoaded && !isAuthenticated && (
                                        <Box sx={{ mt: 2, display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={handleSignUpClick}
                                                startIcon={<StarIcon sx={{ fontSize: 18 }} />}
                                                sx={{
                                                    backgroundColor: "#ff9800",
                                                    "&:hover": { backgroundColor: "#e65100" },
                                                    px: 2,
                                                    py: 0.75,
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                }}
                                            >
                                                {t("core.signup.title")}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </Box>
                </AccordionDetails>
            </Accordion>
            {isAdminRole ? (
                <CrosswordFillCellsBlock
                    wordInputs={wordInputs}
                    localWordInputValues={localWordInputValues}
                    hintRevealCells={hintRevealCells}
                    foundCellKeySet={foundCellKeySet}
                    isAdminRole={isAdminRole}
                    onWordInputChange={onWordInputChange}
                    onWordInputSubmit={onWordInputSubmit}
                    adminGridClueNumberFontPx={adminGridClueNumberFontPx}
                    adminGridCellFontPx={adminGridCellFontPx}
                    onAdminGridClueNumberFontPxChange={onAdminGridClueNumberFontPxChange}
                    onAdminGridCellFontPxChange={onAdminGridCellFontPxChange}
                />
            ) : null}
        </Box>
    );
}
